import type { CampaignData, CAMPAIGN_STATE } from '@/lib/types';

/**
 * Optimistic updater for the campaign-state flip applied via
 * `queryClient.setQueriesData({ queryKey: ['campaigns'] }, ...)`.
 *
 * That filter is a PREFIX match, so the updater also receives the cached value of
 * every non-list query under the `['campaigns']` prefix (e.g.
 * `['campaigns', id, 'tracking-template']`, `'...bid-modifiers'`), which are plain
 * objects, not `CampaignData[]`. Guard on `Array.isArray` before mapping: optional
 * chaining only guards null/undefined, so `old?.map` on one of those objects throws
 * "old?.map is not a function" and crashes the campaign page. Non-array entries are
 * returned unchanged so their own queries keep their cached value.
 */
export function applyCampaignStateToLists(
  cached: unknown,
  campaignId: number,
  target: CAMPAIGN_STATE,
): CampaignData[] | undefined {
  // Non-list queries under the prefix: leave untouched (same reference = no re-render).
  if (!Array.isArray(cached)) return cached as CampaignData[] | undefined;
  return cached.map((c: CampaignData) => (c.id === campaignId ? { ...c, state: target } : c));
}

/**
 * Resolve a campaign's slug from the cached campaign lists returned by
 * `queryClient.getQueriesData({ queryKey: ['campaigns'] })`. Same prefix-match hazard
 * as above: the returned entries include non-array sibling detail queries, so guard on
 * `Array.isArray` before calling `.find` on each entry's data.
 */
export function findCampaignSlugInLists(
  lists: ReadonlyArray<readonly [unknown, unknown]>,
  campaignId: number,
): string | undefined {
  for (const [, data] of lists) {
    if (!Array.isArray(data)) continue;
    const match = data.find((c: CampaignData) => c?.id === campaignId);
    if (match?.slug) return match.slug;
  }
  return undefined;
}
